/**
 * The closed error code registry of section 04, 4.5.5.
 *
 * `R4` of 9.2.2: an `ErrorCode` outside this list does not compile. That is the
 * whole point of the file. A code invented at a call site is a code the frontend
 * has never heard of, and the frontend branches on `code` and never on `message`.
 *
 * The list lives in `packages/contracts` rather than in `platform` because both
 * the backend and the six frontends need it, and `error-codes.spec.ts` asserts
 * it against the table in 04, 4.5.5 so the two cannot drift.
 */
export const ERROR_CODES = [
  // 400
  "invalid_request_body",
  "invalid_query_parameter",
  "invalid_state_transition",
  "missing_reception_channel",
  "motive_required",
  // 401
  "unauthenticated",
  "otp_invalid",
  "pin_invalid",
  // 403
  "forbidden",
  "out_of_scope",
  "read_only_dossier",
  "feature_disabled",
  // 404
  "not_found",
  // 409
  "conflict",
  "stage_guard_failed",
  "capacity_exceeded",
  "insufficient_balance",
  "duplicate_live_file",
  "already_processed",
  // `round C7`, `C7.6`: a chase asked on a seller's pièce , refused, a link never asks a buyer for a seller's paper.
  "seller_document",
  "offboarding_blocked",
  // 410
  "gone",
  // 422
  "hard_block",
  "derogation_required",
  // 428
  "precondition_required",
  // 429
  "rate_limited",
  // 500
  "internal_error",
  "cross_schema_access",
  // 502, 503
  "provider_unavailable",
  // **Two causes that wore one code until 5 September 2026**, and the day they
  // shared it cost three hours. Staging refused every enrolment with
  // `provider_unavailable`, which is ALSO the deliberate answer of a host with
  // no channel bound , so the log read exactly like the honest unbound posture
  // while a live credential was being refused by Twilio with `20003`.
  //
  // `channel_unbound` is a configuration fact about US: nothing is wired, and
  // nothing was sent or attempted. `provider_refused` is an answer from a
  // provider that was really asked. The citizen reads the same sentence for
  // both , the difference is ours and not theirs , but an operator must never
  // again have to guess which of the two a 502 meant.
  "channel_unbound",
  "provider_refused",
  "service_unavailable",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * Codes that must never reach a client. `cross_schema_access` is a programming
 * error (09, 9.3.4 check `B7`): it is logged with its module and model, and the
 * caller is told `internal_error` and nothing more.
 */
export const INTERNAL_ONLY_ERROR_CODES = ["cross_schema_access"] as const;

export type InternalOnlyErrorCode = (typeof INTERNAL_ONLY_ERROR_CODES)[number];

/** The HTTP status 4.5.5 pairs with each code. */
export const ERROR_CODE_STATUS: Readonly<Record<ErrorCode, number>> = {
  invalid_request_body: 400,
  invalid_query_parameter: 400,
  invalid_state_transition: 400,
  missing_reception_channel: 400,
  motive_required: 400,
  unauthenticated: 401,
  otp_invalid: 401,
  pin_invalid: 401,
  forbidden: 403,
  out_of_scope: 403,
  read_only_dossier: 403,
  feature_disabled: 403,
  not_found: 404,
  conflict: 409,
  stage_guard_failed: 409,
  capacity_exceeded: 409,
  insufficient_balance: 409,
  duplicate_live_file: 409,
  already_processed: 409,
  seller_document: 409,
  offboarding_blocked: 409,
  gone: 410,
  hard_block: 422,
  derogation_required: 422,
  precondition_required: 428,
  rate_limited: 429,
  internal_error: 500,
  cross_schema_access: 500,
  provider_unavailable: 502,
  // Both are the same fact to a client , an upstream this API depends on did
  // not produce a message , so both keep 502 and only the code differs.
  channel_unbound: 502,
  provider_refused: 502,
  service_unavailable: 503,
};

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && (ERROR_CODES as readonly string[]).includes(value);
}
